// The in-app report's panel (ADR-0518): the person's words, every item that
// would be sent, each removable, an optional picture of the page they can see
// and remove, and *Send*. Nothing leaves the browser before *Send* (5a).
//
// It is drawn inside `data-report-exclude`, so a picture of the page never
// carries the panel, and its own fields are masked like every form control.
import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";
import { api, idempotencyKey } from "../api/client";
import { describe, headline, type Problem } from "../api/problem";
import { useEstateIfAny, scopeNames } from "../shell/estate";
import { useScopeIfAny } from "../shell/scope";
import { useSession } from "../shell/session";
import { Sheet } from "../surfaces";
import { base64, groups, GROUPS, keptByDefault, request, type Group, type Kind } from "./build";
import { dismiss, sent, snapshot, subscribe } from "./capture";
import { renderScreenshot } from "./screenshot";

const GROUP_LABEL: Record<Group, string> = {
  page: "The page you are on, with its query values removed",
  browser: "Your browser, its window size and language",
  build: "This console's version",
  scope: "The organization, customer and site you are working in, and your role",
  problem: "The last error the server answered",
  calls: "The last requests this page made: method, path, status, time and request id",
  errors: "The last errors this page met, with their stack",
};

function countOf(v: unknown): string {
  return Array.isArray(v) ? ` (${v.length})` : "";
}

export function ReportPanel({ onClose, roleOf }: { onClose(): void; roleOf(scope: { orgId?: string; customerId?: string; siteId?: string }): string | undefined }) {
  const { me } = useSession();
  const location = useLocation();
  const scope = useScopeIfAny()?.scope ?? {};
  const lists = useEstateIfAny();
  const captured = useSyncExternalStore(subscribe, snapshot);
  const [kind, setKind] = useState<Kind>("bug");
  const [text, setText] = useState("");
  const [kept, setKept] = useState<Set<Group>>(() => keptByDefault("bug"));
  const [picture, setPicture] = useState<{ blob: Blob; url: string }>();
  const [drawing, setDrawing] = useState(false);
  const [pictureProblem, setPictureProblem] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<Problem>();
  const [reference, setReference] = useState<string>();

  // What the page holds now, frozen when the panel opens so the list the
  // person reviews is the list that is sent: a request this panel makes is not
  // added to it.
  const [facts] = useState(() => {
    const names = scopeNames(lists, scope);
    const at = (id: string | undefined, name: string | undefined) => (id ? { id, name } : undefined);
    return {
      pathname: location.pathname, search: location.search, title: document.title,
      scope: {
        org: at(scope.orgId, names.org), customer: at(scope.customerId, names.customer), site: at(scope.siteId, names.site),
        role: roleOf(scope),
      },
    };
  });
  const [frozen] = useState(() => captured);
  const available = useMemo(() => groups(facts, frozen), [facts, frozen]);
  const offered = GROUPS.filter((g) => available[g] !== undefined);

  useEffect(() => () => { if (picture) URL.revokeObjectURL(picture.url); }, [picture]);

  const pickKind = (k: Kind) => {
    setKind(k);
    setKept(keptByDefault(k));
  };
  const toggle = (g: Group) => setKept((prev) => {
    const next = new Set(prev);
    if (next.has(g)) next.delete(g); else next.add(g);
    return next;
  });

  const takePicture = async () => {
    setDrawing(true);
    setPictureProblem(undefined);
    try {
      const blob = await renderScreenshot();
      setPicture({ blob, url: URL.createObjectURL(blob) });
    } catch (e) {
      setPictureProblem(e instanceof Error ? e.message : "The picture could not be drawn.");
    } finally {
      setDrawing(false);
    }
  };

  const send = async () => {
    setBusy(true);
    setProblem(undefined);
    try {
      const body = request(kind, text.trim(), available, kept, picture ? await base64(picture.blob) : undefined);
      // One key per press (ADR-0518 8a): a retry the transport makes is this
      // report, and the person's next press is another.
      const { data, error, response } = await api.POST("/me/reports", { params: { header: { "Idempotency-Key": idempotencyKey() } }, body });
      if (data) {
        setReference(data.reference);
        sent();
      } else setProblem(describe(error, response.status));
    } catch (e) {
      setProblem(describe(e));
    } finally {
      setBusy(false);
    }
  };

  const support = me?.brand.support;
  const contact = support && (support.email || support.phone || support.url);

  if (reference) {
    return (
      <div data-report-exclude>
        <Sheet label="Report sent" title="Report sent" onClose={onClose} foot={<button type="button" className="btn btn-primary" onClick={onClose}>Close</button>}>
          <p>Thank you. The team that builds and supports this console has it, as <strong>{reference}</strong>. Quote that reference if you write to them about it.</p>
        </Sheet>
      </div>
    );
  }

  return (
    <div data-report-exclude>
      <Sheet label="Report a problem or ask for something" title="Report a problem or ask for something" onClose={onClose} busy={busy}
        foot={<>
          <button type="button" className="btn btn-primary" disabled={busy || drawing || !text.trim()} onClick={() => void send()}>{busy ? "Sending…" : "Send"}</button>
          <button type="button" className="btn btn-quiet" disabled={busy} onClick={onClose}>Cancel</button>
        </>}>
        {/* Restates ADR-0518 decision 6.3a — one sentence on every host, naming no product
            (ADR-0295), and the brand's own contact for the person's own network. */}
        <p>This goes to the team that builds and supports this console.{contact && <> For help with your own network, contact {contact}.</>}</p>
        {frozen.noted && (
          <p className="report-noted">
            <strong>An error was noted:</strong> {frozen.noted.what}{" "}
            <button type="button" className="btn btn-sm btn-quiet" onClick={dismiss}>Dismiss</button>
          </p>
        )}
        <fieldset className="report-kind">
          <legend>What is this?</legend>
          <label><input type="radio" name="report-kind" checked={kind === "bug"} onChange={() => pickKind("bug")} /> Something is not working</label>
          <label><input type="radio" name="report-kind" checked={kind === "feature"} onChange={() => pickKind("feature")} /> I would like something it does not do</label>
        </fieldset>
        <label className="field">
          <span className="label">{kind === "bug" ? "What happened, and what you expected" : "What you would like, and why"}</span>
          <textarea value={text} maxLength={10000} rows={5} onChange={(e) => setText(e.target.value)} />
        </label>
        <h3>What will be sent</h3>
        {/* Restates ADR-0518 decisions 2a and 7a — every item removable; the server adds who sent it. */}
        <p className="muted">Your name and sign-in address are added when it is sent. Untick anything you would rather not send.</p>
        <ul className="report-items">
          {offered.map((g) => (
            <li key={g}>
              <label><input type="checkbox" checked={kept.has(g)} onChange={() => toggle(g)} /> {GROUP_LABEL[g]}{countOf(available[g])}</label>
              <details><summary>Show</summary><pre className="report-item">{JSON.stringify(available[g], null, 2)}</pre></details>
            </li>
          ))}
        </ul>
        <h3>A picture of the page</h3>
        {picture ? (
          <div className="report-picture">
            <img src={picture.url} alt="The page as it will be sent" />
            <button type="button" className="btn btn-sm" onClick={() => setPicture(undefined)}>Remove the picture</button>
          </div>
        ) : (
          <p>
            <button type="button" className="btn btn-sm" disabled={drawing} onClick={() => void takePicture()}>{drawing ? "Drawing…" : "Add a picture of this page"}</button>{" "}
            {/* Restates ADR-0518 decision 5a — what the picture masks. */}
            <span className="muted">Keys, passphrases, the device console, maps and every field you can type in are drawn as grey boxes.</span>
          </p>
        )}
        {pictureProblem && <p className="muted">{pictureProblem} The report can be sent without it.</p>}
        {problem && <div role="alert" className="problem"><div><strong>{headline(problem)}</strong>{problem.request_id && <div className="problem-id">request {problem.request_id}</div>}</div></div>}
      </Sheet>
    </div>
  );
}
